import { useState } from 'react';
import type { Mensajeria, Plantilla, TipoDePlantilla, VistaPreviaDeMensaje } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import { Enlace } from '../../router';
import {
  AreaTexto, AvisoError, Boton, Cargando, FalloDeCarga, Interruptor, Kpi, Tarjeta, useAvisos,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { pesos, porcentaje } from '../../ui/format';
import { Seccion } from './piezas';

const CUANDO: Record<TipoDePlantilla, string> = {
  confirmacion: 'Al reservar, confirmación inmediata',
  recordatorio: '24 horas antes del turno',
  recordatorio_2h: '2 horas antes del turno',
};
const LARGO_MAXIMO = 320;

export function Recordatorios() {
  const mensajeria = usePedido(() => consultorio.mensajeria(), []);
  const metricas = usePedido(() => consultorio.metricas(8), []);
  const m = metricas.datos;
  return (
    <SeccionPanel titulo="Recordatorios" descripcion="Los recordatorios por WhatsApp son lo que más baja las inasistencias">
      <div className="pila config">
        {m && (
          <div className="kpis">
            <Kpi etiqueta="Ausentismo actual" valor={porcentaje(m.tasa_ausentismo)} icono="user-x" pista="Últimas 8 semanas" />
            <Kpi etiqueta="Perdido por ausencias" valor={pesos(m.perdido)} icono="trending-down" />
            <Kpi etiqueta="Recuperado en señas" valor={pesos(m.recuperado)} icono="shield-check" />
          </div>
        )}
        {mensajeria.estado === 'cargando' && <Cargando />}
        {mensajeria.estado === 'error' && <FalloDeCarga mensaje={mensajeria.error ?? ''} alReintentar={mensajeria.recargar} />}
        {mensajeria.datos && <Editor inicial={mensajeria.datos} />}
      </div>
    </SeccionPanel>
  );
}

function Editor({ inicial }: { inicial: Mensajeria }) {
  const { mostrarAviso } = useAvisos();
  const [plantillas, setPlantillas] = useState<Plantilla[]>(inicial.plantillas);
  const [prueba, setPrueba] = useState<VistaPreviaDeMensaje | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const invalida = plantillas.some((p) => p.texto_template.trim().length < 10);

  const cambiar = (tipo: TipoDePlantilla, cambios: Partial<Plantilla>) =>
    setPlantillas((ps) => ps.map((p) => (p.tipo === tipo ? { ...p, ...cambios } : p)));

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.configurarMensajeria(plantillas);
      mostrarAviso({ tono: 'exito', titulo: 'Recordatorios guardados' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar los recordatorios. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const probar = async (tipo: TipoDePlantilla) => {
    try {
      await consultorio.configurarMensajeria(plantillas);
      setPrueba(await consultorio.probarMensaje(tipo));
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No pudimos armar el mensaje', cuerpo: e instanceof ApiError ? e.message : undefined });
    }
  };

  return (
    <>
      <Tarjeta>
        <Seccion titulo="Número desde el que se envía">
          <p className="nota">
            Los mensajes salen desde el {inicial.telefono}. Lo cambiás en <Enlace a="/pro/perfil">tu perfil</Enlace>.
          </p>
        </Seccion>
      </Tarjeta>
      <Tarjeta>
        <Seccion titulo="Cuándo avisar y qué decir"
          descripcion="Podés usar {paciente}, {profesional}, {fecha} y {hora}. Se reemplazan por los datos de cada turno.">
          <div className="plantillas">
            {plantillas.map((p) => (
              <div key={p.tipo} className="plantilla">
                <Interruptor etiqueta={CUANDO[p.tipo]} checked={p.activo} onChange={(e) => cambiar(p.tipo, { activo: e.target.checked })} />
                <AreaTexto aria-label={`Mensaje: ${CUANDO[p.tipo]}`} rows={3} maxLength={LARGO_MAXIMO} value={p.texto_template}
                  disabled={!p.activo} onChange={(e) => cambiar(p.tipo, { texto_template: e.target.value })} />
                <div className="plantilla__pie">
                  <Boton tamano="chico" variante="fantasma" icono="send" disabled={!p.activo || invalida}
                    onClick={() => void probar(p.tipo)}>Ver cómo llega</Boton>
                </div>
              </div>
            ))}
          </div>
        </Seccion>
      </Tarjeta>
      {prueba && (
        <Tarjeta>
          <Seccion titulo="Así le llega al paciente" descripcion={`Mensaje de ejemplo desde el ${prueba.destino}. El envío real está simulado.`}>
            <p className="burbuja-whatsapp">{prueba.texto}</p>
          </Seccion>
        </Tarjeta>
      )}
      <AvisoError>{error}</AvisoError>
      <div className="acciones-pie">
        <Boton disabled={invalida} cargando={guardando} onClick={() => void guardar()}>Guardar recordatorios</Boton>
      </div>
    </>
  );
}
